import { useCallback } from 'react';
import queryString from 'query-string';
import { dangerouslyCreateSafeString } from '@atlassiansox/analytics';
import { defineMessages } from 'react-intl';

// Generic
import { WelcomeFieldError, URLParameters } from '../../../../types';

// Welcome Components
import { useAppSelector } from '../../../../stores/redux.store';

// Redux
import { showFieldErrors, welcome } from '../../../../actions/welcome-actions';

// Utils
import { getMicrobrandingFromUrl } from '../../../../utilities/applications';
import { useAnalyticsClient } from '../../../../utilities/analytics/analytics-web-client';
import { useAppDispatch } from '../../../../stores/redux.store';
import { signupCaptchaFailure } from '../../../../actions/signup-actions';
import { captureException } from '../../../../utilities/analytics/error-reporting';
import { useRecaptchaContext } from '../../../../contexts/recaptcha';
import { useWelcomePageContext } from '../contexts';
import { useRegisterComponent } from '../../../orchestrator';
import { WELCOME_PAGE_ID } from '../constants';
import { useIntl } from 'react-intl';
import { emailRegex } from '../components';
import { useForceRedirect } from '../../../../utilities/hooks';
import { useNavigateSoftOrHard } from '../../../../utilities/hooks/useNavigateSoftOrHard';
import {
  getMinimumPasswordLength,
  minimumPasswordLengthMessage,
} from '../../../../utilities/password-policy';

const subjectIds: Required<WelcomeFieldError> = {
  displayName: 'welcomeSignupNoName',
  password: 'welcomeSignupNoPassword',
  emailUsedAsName: 'welcomeSignupEmailUsedAsName',
  passwordUsedAsName: 'welcomeSignupPasswordUsedAsName',
};

export type WelcomeFormData = {
  displayName: string;
  password: string;
};

const messages = defineMessages({
  displayNameEmpty: {
    id: 'signup.form.displayName.empty',
    defaultMessage: 'Please enter a name',
  },
  displayNameIsEmail: {
    id: 'signup.form.displayName.is.email',
    defaultMessage: 'Please ensure your full name is not an email address',
  },
  displayNameIsPassword: {
    id: 'signup.form.displayName.is.password',
    defaultMessage: 'Please ensure your full name is not your password',
  },
  passwordEmpty: {
    id: 'signup.form.password.empty',
    defaultMessage: 'Please enter a password',
  },
});

type UseWelcomeSubmissionProps = {
  /**
   * @deprecated
   *
   * When submission is complete, the user will be redirected away from IDAC, via
   * a series of backend redirects, to their product destination with a valid session.
   *
   * We no longer support opting out of this behaviour, to preserve reliability and
   * integrity of user flows & session distribution.
   */
  autoRedirect?: boolean;
};

export const useWelcomeSubmission = ({ autoRedirect = true }: UseWelcomeSubmissionProps = {}) => {
  useRegisterComponent(useWelcomeSubmission.name);

  const navigate = useNavigateSoftOrHard();
  const dispatch = useAppDispatch();
  const analyticsClient = useAnalyticsClient();
  const intl = useIntl();

  const { fetchToken, recaptchaType } = useRecaptchaContext();
  const { marketingConsent } = useWelcomePageContext();
  const csrfToken = useAppSelector(state => state.csrfToken);
  const redirectType = useAppSelector(state => state.welcome.redirectType);
  const error = useAppSelector(state => state.welcome.error);
  const fieldErrors = useAppSelector(state => state.welcome.fieldErrors);
  const formError = useAppSelector(state => state.welcome.formError);
  const isLoading = useAppSelector(state => state.welcome.isLoading) ?? false;
  const redirectTo = useAppSelector(state => state.welcome.redirectTo) ?? null;

  useForceRedirect({ redirectTo: autoRedirect ? redirectTo : undefined });

  const { privacyPolicyText } = useWelcomePageContext();

  const onWelcomeInputValidate = useCallback(
    (data: Partial<WelcomeFormData>) => {
      const validationErrors: WelcomeFieldError = {};
      const { displayName, password } = data;
      const minimumPasswordLength = getMinimumPasswordLength();

      const hasDisplayName = typeof displayName !== 'undefined';
      const hasPassword = typeof password !== 'undefined';

      if (hasDisplayName && displayName.trim() === '') {
        validationErrors.displayName = intl.formatMessage(messages.displayNameEmpty);
      }

      if (hasPassword) {
        if (password.trim() === '') {
          validationErrors.password = intl.formatMessage(messages.passwordEmpty);
        } else if (password.length < minimumPasswordLength) {
          validationErrors.password = intl.formatMessage(minimumPasswordLengthMessage, {
            minimumPasswordLength,
          });
        }
      }

      if (hasDisplayName && emailRegex.test(displayName.trim())) {
        validationErrors.emailUsedAsName = intl.formatMessage(messages.displayNameIsEmail);
      }

      if (
        hasDisplayName &&
        hasPassword &&
        displayName.toLowerCase().trim() === password.toLowerCase().trim()
      ) {
        validationErrors.passwordUsedAsName = intl.formatMessage(messages.displayNameIsPassword);
      }

      return validationErrors;
    },
    [intl]
  );

  const onWelcomeSubmit = useCallback(
    async (data: WelcomeFormData) => {
      try {
        if (!privacyPolicyText) {
          console.error('Form cannot be submitted without showing Privacy Policy');
          return;
        }

        if (!marketingConsent) {
          console.error('Form cannot be submitted without showing Marketing Consent');
          return;
        }

        const validationErrors = onWelcomeInputValidate(data);

        if (Object.keys(validationErrors).length) {
          Object.keys(validationErrors).forEach(errorKey =>
            analyticsClient.errorShownEvent(
              WELCOME_PAGE_ID,
              subjectIds[errorKey] || 'welcomeSignupUnknownUiError'
            )
          );

          dispatch(showFieldErrors(validationErrors));
          return;
        }

        const challengeResponse = await fetchToken();
        const { signature, token } = queryString.parse(location.search) as URLParameters;

        const welcomeToken = token || signature;
        const query = { ...queryString.parse(location.search) };

        navigate(`/signup/welcome?${queryString.stringify(query)}`);

        const microbrandingParams = getMicrobrandingFromUrl(location);

        analyticsClient.formSubmittedEvent(WELCOME_PAGE_ID, 'welcomeForm', {
          marketingConsent: dangerouslyCreateSafeString(marketingConsent.state),
          redirectType,
          recaptchaType,
        });

        await dispatch(
          welcome({
            ...data,
            marketingConsentState: marketingConsent,
            privacyPolicyText,
            microbrandingParams,
            challengeResponse,
            password: data.password || '',
            token: welcomeToken || '',
            // TODO: Should not need this non-null assertion
            csrfToken: csrfToken!,
          })
        );
      } catch (e) {
        captureException(e);
        dispatch(signupCaptchaFailure(navigator.onLine));
      }
    },
    [
      analyticsClient,
      csrfToken,
      dispatch,
      marketingConsent,
      navigate,
      onWelcomeInputValidate,
      privacyPolicyText,
      fetchToken,
      recaptchaType,
      redirectType,
    ]
  );

  return {
    onWelcomeSubmit,
    onWelcomeInputValidate,
    isLoading,
    redirectTo,
    redirectType,
    formError,
    fieldErrors,
    error,
  };
};
